import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { bindFormModule } from '../../shared/formBinding.js';
import { formModalSaved, isFormModalOpen } from '../../shared/formModal.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// The fields the server refuses to take from a client, or that need a shape
// FormData cannot produce. Everything else goes across as typed.
//
// TOGGLES are the single on/off ones, read by id - whether they render as a
// daisyUI toggle or a checkbox, since an unchecked box of either kind is simply
// absent from FormData. MULTI_VALUE are the two rendered as a group of
// checkboxes sharing one name - an array either way, so the API contract did
// not change when they stopped being <select multiple>.
const TOGGLES = ['temporaer', 'festanstellung', 'personalzimmer', 'keineProaktivenVorschlaege'];
const MULTI_VALUE = ['betriebsarten'];

// The shifts are a group of checkboxes like the ones above, but five
// independent booleans rather than one array - each posts its own field name.
// So they need the same absent-means-false handling as TOGGLES, and cannot be
// listed beside them: their ids are prefixed (`schicht-frueh` for the field
// `schichtFrueh`), and naming them here would be a second copy of the catalogue
// in utils/schichten.js that a sixth shift would leave behind. The group is
// read out of the DOM instead, so the server stays the only place the set is
// written down.
const SCHICHT_GRUPPE = 'schichten-gruppe';

// The refs an empty option means "none" for. Sending '' would fail the
// isMongoId check, so the key comes off entirely and the server keeps or clears
// the value as its own rules dictate.
const OPTIONAL_REFS = ['gruppe', 'pflegedokSystem', 'blacklistGrund'];

// Refs whose empty option means "clear this", not "leave it alone". They go
// across as null rather than being dropped: dropping the key is how a PATCH says
// "unchanged", so an OPTIONAL_REFS field can be set and changed but never
// emptied again from the form.
//
// `konzern` needs the other behaviour, because removing a location from a group
// is an everyday edit - it is what the 409 on deleting a Konzern tells the
// reader to go and do. The validators accept a null (`optional({ values:
// 'falsy' })`) and the schema stores it, so null is the whole mechanism.
const CLEARABLE_REFS = ['konzern'];

// Not refs, but the same mechanism and the same reason: "Kein Rating" is a
// state somebody sets deliberately, so the empty option has to clear a rating
// that is already there rather than leave it alone. Sent as null, which the
// validator's sanitizer and the schema's enum both accept - '' would be
// refused by the enum, which is the whole point of not sending it.
const CLEARABLE_ENUMS = ['rating'];

// Optional free-text fields. An empty one is sent as '' rather than dropped:
// dropping it would mean "leave unchanged" on a PATCH, so clearing an address
// in the form would never reach the record.
const NULLABLE_TEXT = [
  'uidNr',
  'strasse',
  'plz',
  'ort',
  'easytempNrAll',
  'easytempNrStaffers',
  'personalzimmerInfo'
];

// Turns the form into the JSON body the API expects.
function readForm(form) {
  const formData = new FormData(form);
  const data = Object.fromEntries(formData.entries());

  // An unchecked checkbox is not submitted at all, so the absent key is the
  // "false" the server needs to see - FormData cannot express it.
  TOGGLES.forEach((field) => {
    data[field] = form.querySelector(`#${field}`)?.checked ?? false;
  });

  // Object.fromEntries keeps only the last value of a repeated key, which here
  // would silently send one of the boxes the user ticked. getAll collects them
  // all - and an empty array when none are ticked, which is what clears the
  // list on a PATCH rather than leaving it unchanged.
  MULTI_VALUE.forEach((field) => {
    data[field] = formData.getAll(field);
  });

  // Every box in the group, ticked or not, keyed by the name it renders with.
  // A ticked one is already in `data` as the string 'on', which fails the
  // isBoolean check server-side, so this overwrites rather than only filling in
  // the absent ones.
  form.querySelectorAll(`#${SCHICHT_GRUPPE} input[type="checkbox"]`).forEach((box) => {
    data[box.name] = box.checked;
  });

  OPTIONAL_REFS.forEach((field) => {
    if (!data[field]) delete data[field];
  });

  [...CLEARABLE_REFS, ...CLEARABLE_ENUMS].forEach((field) => {
    if (data[field] !== undefined && !data[field]) data[field] = null;
  });

  NULLABLE_TEXT.forEach((field) => {
    if (data[field] !== undefined) data[field] = data[field].trim();
  });

  return data;
}

// The two blocks that appear and disappear with the control above them. The
// server renders the right state for the record as loaded; this keeps it right
// as someone edits.
function initConditionalBlocks(form) {
  const personalzimmer = form.querySelector('#personalzimmer');
  const personalzimmerInfo = form.querySelector('#personalzimmer-info-feld');

  if (personalzimmer && personalzimmerInfo) {
    personalzimmer.addEventListener('change', () => {
      personalzimmerInfo.hidden = !personalzimmer.checked;
    });
  }

  const status = form.querySelector('#status');
  const blacklist = form.querySelector('#blacklist-block');

  if (status && blacklist) {
    status.addEventListener('change', () => {
      blacklist.hidden = status.value !== 'blacklist';
      // Moving the picker away from blacklist clears the reason too, so a
      // hidden field cannot submit a value the server would then reject as
      // inconsistent with the status beside it.
      if (blacklist.hidden) {
        const grund = form.querySelector('#blacklistGrund');
        if (grund) grund.value = '';
      }
    });
  }
}

async function submit(form) {
  const submitButton = form.querySelector('button[type="submit"]');
  const { kundeId } = form.dataset;
  const data = readForm(form);

  if (submitButton) submitButton.disabled = true;
  showLoading('Wird gespeichert …');

  try {
    const { data: body } = kundeId
      ? await http.patch(`/kunden/${kundeId}`, data)
      : await http.post('/kunden', data);

    // Editing in the dialog stays where it was: the page behind it - the list
    // or the customer's own page - is already the right place, so it closes and
    // refetches rather than navigating.
    if (kundeId && isFormModalOpen()) {
      hideLoading();
      // Before formModalSaved, not after: the caller's onSaved may navigate -
      // a detail page reloads itself - and Notiflix then paints into a document
      // that is being torn down, which throws into the catch below and shows an
      // error for a save that worked.
      notifySuccess('Kunde wurde aktualisiert.');
      formModalSaved();
      return;
    }

    // Creating goes to the new customer either way. It is the record someone is
    // about to keep working on, and unlike the other resources there is more to
    // it than the row that would appear in the list.
    const id = kundeId || body.data.kunde.id;
    window.location.href = withFlash(
      `/kunden/${id}`,
      kundeId ? 'kunde-aktualisiert' : 'kunde-erstellt'
    );
  } catch (err) {
    hideLoading();
    if (submitButton) submitButton.disabled = false;

    // The duplicate-UID 409 names the customer already holding the number, so
    // it is worth showing on the field rather than only as a toast that
    // disappears.
    const message = err.response?.data?.message;
    if (err.response?.status === 409 && message) {
      const uid = form.querySelector('#uidNr');
      if (uid) {
        uid.setCustomValidity(message);
        uid.reportValidity();
        // Cleared on the next edit, or the browser would keep refusing to
        // submit a value the user has since corrected.
        uid.addEventListener('input', () => uid.setCustomValidity(''), { once: true });
      }
    }

    notifyError(message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.');
  }
}

export function initKundeForm() {
  bindFormModule('kunde-form', submit, initConditionalBlocks);
}
